import * as React from 'react';
import { ChangeEvent, useEffect, useState } from 'react';
import { TChemicalState } from '../../types/Chemical';
import { useTranslation } from 'react-i18next';
import FirstAidMeasurements from './panels/FirstAidMeasurements';
import Hazards from './panels/Hazards';
import Company from './panels/Company';
import { simpleDateFormat } from '../../Helpers';
import HandlingAndStorage from './panels/HandlingAndStorage';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import ProtectiveMeasurements from './panels/ProtectiveMeasurements';
import { fetchWithAuthentication } from '../../actions/BaseAction';
import Exposure from './panels/Exposure';
import FireFighting from './panels/FireFighting';
import Spills from './panels/Spills';
import makeStyles from '@mui/styles/makeStyles';
import Emergency from './panels/Emergency';
import Process from './panels/Process';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from '../../store/Store';
import { TAuthState } from '../../types/Auth';
import { TDispatch } from '../../types/Thunk';
import { uploadChemicalImage } from '../../actions/ChemicalActions';
import { useUser } from '../../hooks/useUser';
import EntityImageCapture from '../EntityImageCapture';

interface IChemicalProductProps {
  chemical: TChemicalState;
  expandedFirstAid: boolean;
  expandedHazards: boolean;
  expandedProducer: boolean;
  expandedHandling: boolean;
  expandedExposure: boolean;
  expandedFireFighting: boolean;
  expandedSpills: boolean;
  expandedEmergency: boolean;
  expandedProcess: boolean;
  handleChange: (
    panel: string
  ) => ((event: ChangeEvent<{}>, expanded: boolean) => void) | undefined;
}

const useStyles = makeStyles({
  riskContainer: {
    textAlign: 'center',
    borderRadius: '5px',
    padding: '10px',
    marginTop: '15px',
  },
  riskLabel: {
    fontWeight: 'bold',
    color: '#0000008a',
    marginBottom: '5px',
    textTransform: 'uppercase',
  },
  riskSecondary: {
    marginTop: '5px',
    color: '#0000008a',
  }
});

const ChemicalProduct: React.FC<IChemicalProductProps> = ({
  chemical,
  expandedHazards,
  expandedProducer,
  expandedFirstAid,
  expandedExposure,
  expandedFireFighting,
  expandedSpills,
  expandedEmergency,
  expandedProcess,
  handleChange,
  expandedHandling,
}): JSX.Element => {
  const { t } = useTranslation();
  const [safetyDataSheet, setsafetyDataSheet] = useState('*');
  const classes = useStyles({});
  const authState: TAuthState = useSelector<IAppState, TAuthState>(
    (state) => state.authState
  );
  const dispatch = useDispatch<TDispatch>();
  const { isImpersonated } = useUser();

  useEffect(() => {
    if (chemical.activeChemical?.data.safetyDataSheet) {
      fetchWithAuthentication(chemical.activeChemical?.data.safetyDataSheet)
        .then(blob => {
          if (blob !== null) {
            setsafetyDataSheet(URL.createObjectURL(blob));
          }
        })
        .catch(error => {throw error});
    }
  }, [chemical.activeChemical]);

  const getRevisedDate = (date: Date) => {
    return simpleDateFormat(date);
  };

  if (!chemical.activeChemical?.data.id) {
    return <></>;
  }

  return (
    <>
      {chemical && (
        <>
          {chemical.activeChemical?.data.name && (
            <h2>{chemical.activeChemical?.data.name as string}</h2>
          )}
          <EntityImageCapture
            imageUrl={chemical.activeChemical?.data.image as string}
            canEdit={Boolean(authState.data?.permissions?.canEditChemicalImages) && !isImpersonated()}
            placeholderIconClass='fas fa-flask'
            widthOnDesktop='35%'
            onUpload={(dataUrl) =>
              dispatch(uploadChemicalImage(Number(chemical.activeChemical?.data.id), dataUrl))
            }
            altText={chemical.activeChemical?.data.name as string}
            imageMaxHeight={300}
            imageObjectFit='contain'
          />

          {
            chemical.activeChemical?.data.riskAssessed &&
            chemical.activeChemical?.data.risk &&
            (
            <div className={classes.riskContainer} style={{
              backgroundColor: chemical.activeChemical?.data.riskColor,
            }}>
              <p className={classes.riskLabel}>
                {t(chemical.activeChemical.data.risk)}
              </p>
              {chemical.activeChemical?.data.riskDate && (
                <p className={classes.riskSecondary}>{t('chemical_risk_score.assessed_date')}: {getRevisedDate(
                  chemical.activeChemical.data.riskDate
                ) as string}</p>
              )}
            </div>
          )}

          {chemical.activeChemical && (
            <ProtectiveMeasurements
              exposureControls={chemical.activeChemical.data.protectiveEquipment as string}
              recommendedUse={chemical.activeChemical.data.recommendedUse as string}
              ppe={chemical.activeChemical.ppe}
              hazards={chemical.activeChemical.hazards}
              signalWordType={chemical?.activeChemical?.data.signalWordType}
            />
          )}

          {
            (chemical?.activeChemical?.data.vendor ||
            chemical?.activeChemical?.data.emergencyInformation ||
            chemical?.activeChemical?.data.emergencyPhone) && (
            <Emergency
              emergencyInformation={chemical?.activeChemical?.data.emergencyInformation}
              emergencyPhone={chemical?.activeChemical?.data.emergencyPhone}
              vendor={chemical.activeChemical.data.vendor}
              expanded={expandedEmergency}
              handleChange={handleChange}
            />
          )}

          {(
            chemical?.activeChemical?.data.hazardStatements ||
            chemical?.activeChemical?.data.precautionaryStatements ||
            chemical?.activeChemical?.data.otherHazards
          ) && (
              <Hazards
                hazardClassification={chemical?.activeChemical.data.hazardClassification}
                hazardStatements={chemical?.activeChemical?.data.hazardStatements}
                precautionaryStatements={chemical?.activeChemical?.data.precautionaryStatements}
                otherHazards={chemical?.activeChemical?.data.otherHazards}
                expanded={expandedHazards}
                handleChange={handleChange}
              />
            )}

          {(
            chemical.activeChemical?.data.firstAidMeasures ||
            chemical.activeChemical?.data.symptomsOrEffects ||
            chemical.activeChemical?.data.recommendations
          ) && (
            <FirstAidMeasurements
              expanded={expandedFirstAid}
              firstAidMeasures={chemical.activeChemical?.data.firstAidMeasures}
              symptomsOrEffects={chemical.activeChemical?.data.symptomsOrEffects}
              recommendations={chemical.activeChemical?.data.recommendations}
              handleChange={handleChange}
            />
          )}

          {(
            chemical.activeChemical?.data.extinguishingMedia ||
            chemical.activeChemical?.data.specialFireHazards ||
            chemical.activeChemical?.data.adviceForFirefighters
          ) && (
            <FireFighting
              expanded={expandedFireFighting}
              extinguishingMedia={chemical.activeChemical?.data.extinguishingMedia}
              specialFireHazards={chemical.activeChemical?.data.specialFireHazards}
              adviceForFirefighters={chemical.activeChemical?.data.adviceForFirefighters}
              handleChange={handleChange}
            />
          )}

          {(
            chemical.activeChemical?.data.personalPrecautions ||
            chemical.activeChemical?.data.environmentalPrecautions ||
            chemical.activeChemical?.data.containmentAndCleanUp
          ) && (
            <Spills
              expanded={expandedSpills}
              personalPrecautions={chemical.activeChemical?.data.personalPrecautions}
              environmentalPrecautions={chemical.activeChemical?.data.environmentalPrecautions}
              containmentAndCleanUp={chemical.activeChemical?.data.containmentAndCleanUp}
              handleChange={handleChange}
            />
          )}

          {(
            chemical.activeChemical?.data.handlingAndStorage ||
            chemical.activeChemical?.data.storage ||
            chemical.activeChemical?.data.disposalConsiderations ||
            chemical.activeChemical?.data.specificEndUses
          ) && (
            <HandlingAndStorage
              expanded={expandedHandling}
              handlingAndStorage={chemical.activeChemical?.data.handlingAndStorage}
              storage={chemical.activeChemical?.data.storage}
              specificEndUse={chemical.activeChemical?.data.specificEndUses}
              disposalConsiderations={chemical.activeChemical?.data.disposalConsiderations}
              handleChange={handleChange}
            />
          )}

          {(chemical.activeChemical?.data.controlParameters && false) && (
            <Exposure
              expanded={expandedExposure}
              controlParameters={chemical.activeChemical?.data.controlParameters}
              handleChange={handleChange}
            />
          )}

          {(chemical.activeChemical?.data.processes && chemical.activeChemical?.data.processes.length > 0) && (
            <Process
              processes={chemical.activeChemical.data.processes}
              expanded={expandedProcess}
              handleChange={handleChange}
              />
          )}

          {(chemical?.activeChemical?.data.productIdentifier ||
            chemical?.activeChemical?.data.revisedDate ||
            chemical?.activeChemical?.data.locations
          ) && (
            <Company
              title={t('chemicals.additional_information')}
              productIdentifier={chemical.activeChemical.data.productIdentifier}
              locations={chemical.activeChemical.data.locations}
              revisedDate={getRevisedDate(
                chemical.activeChemical.data.revisedDate
              ) as string}
              expanded={expandedProducer}
              handleChange={handleChange}
              panelId={4}
            />
          )}

          {chemical.activeChemical?.data.safetyDataSheet && (
            <Grid container justifyContent="space-around">
              <Grid item xs={12}>
                <Button
                  type='button'
                  fullWidth
                  variant='contained'
                  color='primary'
                  target={'_blank'}
                  style={{
                    marginTop: '15px',
                    borderRadius: '15px',
                  }}
                  href={safetyDataSheet}
                  download
                >
                  {t('chemical.download.sds')}
                </Button>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </>
  );
};

export default ChemicalProduct;
